import Text from "../../../Text/index.tsx";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog.tsx";
import { useEffect, type FC } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../ui/form.tsx";
import { Button } from "../../../ui/button.tsx";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group.tsx";
import { triggerEvent } from "@/lib/utils.ts";
import { useTranslation } from "react-i18next";

const formSchema = z.object({
  store: z.string(),
});

type Props = {
  onChangeValue: (name: string, value: string | string[]) => void;
};

const StepTwo: FC<Props> = ({ onChangeValue }) => {
  const { t } = useTranslation();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    onChangeValue("numberOfLocations", values.store);
  };

  useEffect(() => {
    triggerEvent("Onboarding Step Viewed", {
      step_name: "Step Two - Number of Stores",
      step_index: 2,
    });

    return () => {
      triggerEvent("Onboarding Step Completed", {
        step_name: "Step Two - Number of Stores",
        step_index: 2,
      });
    };
  }, []);

  return (
    <DialogContent showCloseButton={false}>
      <VisuallyHidden>
        <DialogTitle>Onboarding</DialogTitle>
      </VisuallyHidden>
      <DialogHeader>
        <VisuallyHidden>
          <DialogDescription>Onboarding, what's your job</DialogDescription>
        </VisuallyHidden>
        <img
          src="/svg/krunsh-logo.svg"
          alt="krunsh logo"
          height={36}
          width={120}
        />
        <div className="flex flex-col gap-[4px]">
          <Text fontWeight={600} fontSize={20} color="#44546F">
            {t("start_with_krunsh")}
          </Text>
          <Text fontWeight={400} fontSize={14} color="#44546F">
            {t("start_with_onboarding")}
          </Text>
        </div>

        <div className="flex flex-col mb-[24px] mt-[36px]">
          <Text fontSize={12} fontWeight={400}>
            {t("onboarding_step_1_title")}
          </Text>
          <Text fontSize={20} fontWeight={600}>
            {t("onboarding_step_2_description")}
          </Text>
        </div>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <FormField
            control={form.control}
            name="store"
            render={({ field }) => (
              <FormItem className="space-y-[12px]">
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex flex-col w-full"
                  >
                    <FormItem className="flex items-center w-full border border-[#E9D8D8] rounded-[8px] p-[12px]">
                      <FormControl>
                        <RadioGroupItem value="no_store" />
                      </FormControl>
                      <FormLabel>
                        <Text fontSize={14} fontWeight={400}>
                          {t("no_yet_store")}
                        </Text>
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center w-full border border-[#E9D8D8] rounded-[8px] p-[12px]">
                      <FormControl>
                        <RadioGroupItem value="one_store" />
                      </FormControl>
                      <FormLabel>
                        <Text fontSize={14} fontWeight={400}>
                          {t("one_store")}
                        </Text>
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center w-full border border-[#E9D8D8] rounded-[8px] p-[12px]">
                      <FormControl>
                        <RadioGroupItem value="two_stores" />
                      </FormControl>
                      <FormLabel>
                        <Text fontSize={14} fontWeight={400}>
                          {t("two_stores")}
                        </Text>
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center w-full border border-[#E9D8D8] rounded-[8px] p-[12px]">
                      <FormControl>
                        <RadioGroupItem value="between_3_and_5_stores" />
                      </FormControl>
                      <FormLabel>
                        <Text fontSize={14} fontWeight={400}>
                          {t("between_3_and_5_stores")}
                        </Text>
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center w-full border border-[#E9D8D8] rounded-[8px] p-[12px]">
                      <FormControl>
                        <RadioGroupItem value="more_5_stores" />
                      </FormControl>
                      <FormLabel>
                        <Text fontSize={14} fontWeight={400}>
                          {t("more_5_stores")}
                        </Text>
                      </FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            size="lg"
            type="submit"
            variant="primaryFilled"
            className="w-full mt-[24px]"
            disabled={!form.formState.isValid}
          >
            {t("next")}
          </Button>
        </form>
      </Form>
    </DialogContent>
  );
};

export default StepTwo;
